import { Calculator, Check, FileSignature, Hourglass, IdCard, Landmark, Receipt, ShieldAlert, X } from "lucide-react";
import { cn } from "@/lib/utils";
const TONES = {
  fraud: { ring: "text-destructive", tile: "bg-destructive/10 text-destructive", badge: "bg-destructive text-white", Icon: ShieldAlert, label: "Fraud flag" },
  valid: { ring: "text-success", tile: "bg-success/10 text-success", badge: "bg-success text-white", Icon: Check, label: "Valid" },
  invalid: { ring: "text-destructive", tile: "bg-destructive/10 text-destructive", badge: "bg-destructive text-white", Icon: X, label: "Invalid" },
  review: { ring: "text-warning", tile: "bg-warning/10 text-warning", badge: "bg-warning text-white", mark: "!", label: "In review" },
  progress: { ring: "text-brand-strong", tile: "bg-brand/40 text-foreground dark:bg-brand/15 dark:text-brand", badge: "bg-background text-muted-foreground ring-1 ring-border", Icon: Hourglass, label: "In progress" },
  waiting: { ring: "text-muted-foreground/40", tile: "bg-muted text-muted-foreground", label: "Not started" },
};
const OUTCOME_TONE = { pass_documents_only: "valid", does_not_meet: "invalid", needs_review: "review" };
const toneOf = (a) => {
  if (a.fraud_flag) return "fraud";
  if (a.status === "documents_complete") return OUTCOME_TONE[a.outcome] || "review";
  return a.status === "in_progress" ? "progress" : "waiting";
};
const R = 21;
const C = 2 * Math.PI * R;
/** The applicant's letter, tinted by their result, inside a ring that fills as their documents come in. Never a photo of the person. */
export const ApplicantMark = ({ a, testid }) => {
  const tone = TONES[toneOf(a)];
  const total = a.items_total || 0;
  const share = total ? (a.items_done || 0) / total : 0;
  const label = a.label || "";
  const letter = /^Applicant\s+/i.test(label)
    ? label.replace(/^Applicant\s+/i, "").slice(0, 2)
    : label.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  const { Icon } = tone;
  return (
    <span className="relative grid size-12 shrink-0 place-items-center" role="img"
      aria-label={`${a.label}: ${tone.label}, ${a.items_done || 0} of ${total} documents`} data-testid={testid}>
      <svg viewBox="0 0 48 48" className={cn("absolute inset-0 -rotate-90", tone.ring)} aria-hidden="true">
        <circle cx="24" cy="24" r={R} fill="none" strokeWidth="2.5" className="stroke-muted" strokeDasharray={total ? undefined : "2 3"} />
        {share > 0 && (
          <circle cx="24" cy="24" r={R} fill="none" strokeWidth="2.5" strokeLinecap="round" stroke="currentColor"
            strokeDasharray={C} strokeDashoffset={C * (1 - share)} className="transition-[stroke-dashoffset] duration-500" />
        )}
      </svg>
      <span className={cn("grid size-9 place-items-center rounded-full text-[15px] font-semibold", tone.tile)} aria-hidden="true">{letter}</span>
      {(Icon || tone.mark) && (
        <span className={cn("absolute -bottom-0.5 -right-0.5 grid size-[18px] place-items-center rounded-full border-2 border-background", tone.badge)} aria-hidden="true">
          {Icon ? <Icon className="size-2.5" strokeWidth={3} /> : <span className="text-[10px] font-bold leading-none">{tone.mark}</span>}
        </span>
      )}
    </span>
  );
};
const KIND_ICON = { photo_id: IdCard, employment_contract: FileSignature, payslip: Receipt, sa302: Calculator, bank_statement: Landmark };
const PERSON = { guarantor: "G", sharer: "S" };
const STATE = {
  done: ["bg-success/10 text-success", "accepted"],
  explained: ["bg-warning/10 text-warning", "missing, with an explanation"],
  review: ["bg-warning/10 text-warning", "needs a closer look"],
  fixing: ["bg-destructive/10 text-destructive", "needs fixing"],
  checking: ["bg-warning/10 text-warning motion-safe:animate-pulse", "being checked"],
  todo: ["border border-dashed text-muted-foreground/60", "not sent yet"],
};
/** One small stamp per document on the applicant's checklist, filled in as each is accepted. */
export const DocStamps = ({ items = [], testid }) => (
  <span className="flex flex-wrap items-center justify-end gap-1.5" data-testid={testid}>
    {items.map((item) => {
      const Icon = KIND_ICON[item.kind] || Receipt;
      const [cls, said] = STATE[item.state] || STATE.todo;
      return (
        <span key={item.key} className={cn("relative grid size-6 place-items-center rounded-md", cls)}
          title={`${item.title}: ${said}`} aria-label={`${item.title}: ${said}`} data-state={item.state}>
          <Icon className="size-3.5" strokeWidth={2} />
          {PERSON[item.person] && (
            <span className="absolute -right-1 -top-1 grid size-3 place-items-center rounded-full bg-background text-[8px] font-bold leading-none text-foreground ring-1 ring-border">
              {PERSON[item.person]}
            </span>
          )}
        </span>
      );
    })}
  </span>
);
